/**
 * Create / edit a dataset relationship (L2 knowledge graph, ADR-0026 §4).
 *
 * Pick the two endpoints (dataset → field on each side) + cardinality + a name.
 * On edit the endpoints are immutable (the backend re-creates to move an edge),
 * so the dataset pickers are disabled in edit mode.
 */
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Loader2 } from "lucide-react";
import * as React from "react";
import { useTranslation } from "react-i18next";

import { Alert } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { Combobox, type ComboboxOption } from "@/components/ui/combobox";
import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { datasetsApi } from "@/lib/api-datasets";
import {
  CARDINALITY_GLYPH,
  relationshipsApi,
  type Cardinality,
  type Relationship,
} from "@/lib/api-relationships";

const CARDINALITIES: Cardinality[] = ["one_to_one", "one_to_many", "many_to_one", "many_to_many"];

export interface RelationshipDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Default "from" dataset (the editor's dataset). */
  fromDatasetId: string;
  /** When set, edit that edge instead of creating. */
  editing?: Relationship | null;
}

function useFields(datasetId: string | null) {
  return useQuery({
    queryKey: ["datasets", datasetId, "fields"],
    queryFn: () => datasetsApi.fields(datasetId!),
    enabled: Boolean(datasetId),
  });
}

export function RelationshipDialog({
  open,
  onOpenChange,
  fromDatasetId,
  editing,
}: RelationshipDialogProps) {
  const { t } = useTranslation();
  const qc = useQueryClient();

  const datasetsQ = useQuery({ queryKey: ["datasets"], queryFn: () => datasetsApi.list() });

  const [name, setName] = React.useState("");
  const [fromDs, setFromDs] = React.useState(fromDatasetId);
  const [fromField, setFromField] = React.useState<string | null>(null);
  const [toDs, setToDs] = React.useState<string | null>(null);
  const [toField, setToField] = React.useState<string | null>(null);
  const [cardinality, setCardinality] = React.useState<Cardinality>("many_to_one");

  // (Re)seed when the dialog opens — for create or edit.
  React.useEffect(() => {
    if (!open) return;
    if (editing) {
      setName(editing.name);
      setFromDs(editing.from_dataset_id);
      setFromField(editing.from_field);
      setToDs(editing.to_dataset_id);
      setToField(editing.to_field);
      setCardinality(editing.cardinality);
    } else {
      setName("");
      setFromDs(fromDatasetId);
      setFromField(null);
      setToDs(null);
      setToField(null);
      setCardinality("many_to_one");
    }
  }, [open, editing, fromDatasetId]);

  const fromFieldsQ = useFields(fromDs);
  const toFieldsQ = useFields(toDs);

  const datasetOptions: ComboboxOption[] = (datasetsQ.data ?? []).map((d) => ({
    value: d.id,
    label: d.name,
  }));
  const fieldOpts = (rows?: { name: string }[]): ComboboxOption[] =>
    (rows ?? []).map((f) => ({ value: f.name, label: f.name }));
  const cardOptions: ComboboxOption[] = CARDINALITIES.map((c) => ({
    value: c,
    label: `${CARDINALITY_GLYPH[c]} · ${t(`relationships.cardinality.${c}`, c)}`,
  }));

  const save = useMutation({
    mutationFn: () => {
      if (editing) {
        return relationshipsApi.update(editing.id, {
          name,
          from_field: fromField ?? editing.from_field,
          to_field: toField ?? editing.to_field,
          cardinality,
        });
      }
      return relationshipsApi.create({
        name,
        from_dataset_id: fromDs,
        from_field: fromField!,
        to_dataset_id: toDs!,
        to_field: toField!,
        cardinality,
      });
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["relationships"] });
      onOpenChange(false);
    },
  });

  const valid = name.trim() && fromDs && fromField && toDs && toField;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle className="text-sm font-semibold">
            {editing
              ? t("relationships.dialog.editTitle", "Modifica relazione")
              : t("relationships.dialog.createTitle", "Nuova relazione")}
          </DialogTitle>
        </DialogHeader>
        <DialogBody className="space-y-3">
          <Field label={t("relationships.field.name", "Nome")}>
            <Input
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder={t("relationships.field.namePh", "es. prezzi dell'articolo")}
            />
          </Field>

          <div className="grid grid-cols-2 gap-3">
            <Field label={t("relationships.field.from", "Da (dataset)")}>
              <Combobox
                options={datasetOptions}
                value={fromDs}
                onChange={(v) => {
                  setFromDs(v ?? "");
                  setFromField(null);
                }}
                disabled={Boolean(editing)}
              />
            </Field>
            <Field label={t("relationships.field.fromField", "Campo")}>
              <Combobox
                options={fieldOpts(fromFieldsQ.data)}
                value={fromField}
                onChange={setFromField}
                allowCustom
                placeholder="—"
              />
            </Field>
            <Field label={t("relationships.field.to", "A (dataset)")}>
              <Combobox
                options={datasetOptions}
                value={toDs}
                onChange={(v) => {
                  setToDs(v);
                  setToField(null);
                }}
                disabled={Boolean(editing)}
                placeholder="—"
              />
            </Field>
            <Field label={t("relationships.field.toField", "Campo")}>
              <Combobox
                options={fieldOpts(toFieldsQ.data)}
                value={toField}
                onChange={setToField}
                allowCustom
                placeholder="—"
              />
            </Field>
          </div>

          <Field label={t("relationships.field.cardinality", "Cardinalità")}>
            <Combobox
              options={cardOptions}
              value={cardinality}
              onChange={(v) => v && setCardinality(v as Cardinality)}
            />
          </Field>

          {save.isError && (
            <Alert tone="danger" title={t("common.saveFailed", "Salvataggio fallito")}>
              {(save.error as Error).message}
            </Alert>
          )}
        </DialogBody>
        <DialogFooter>
          <Button variant="ghost" size="sm" onClick={() => onOpenChange(false)}>
            {t("common.cancel", "Annulla")}
          </Button>
          <Button size="sm" disabled={!valid || save.isPending} onClick={() => save.mutate()}>
            {save.isPending && <Loader2 className="h-4 w-4 animate-spin" strokeWidth={1.5} />}
            {t("common.save", "Salva")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <label className="block space-y-1">
      <span className="text-xs font-medium text-muted-foreground">{label}</span>
      {children}
    </label>
  );
}
